import React, { useState, useEffect } from 'react';
import { communityAPI } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import {
  Home, Utensils, Heart, Microscope, Smile, Dna,
  Building, Megaphone, Users, ExternalLink, ArrowRight
} from 'lucide-react';

const iconMap = { Home, Utensils, Heart, Microscope, Smile, Dna, Building, Megaphone, Users };

const CommunityConnect = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await communityAPI.getAll();
        setProjects(data || []);
      } catch {
        setError('Failed to load community projects');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <section id="community" className="py-20 px-4 bg-gradient-to-b from-white via-rose-50/30 to-white" data-testid="community-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-600 to-pink-600 text-white rounded-full text-sm font-semibold mb-4 shadow-lg">
            <Heart className="w-5 h-5" />
            <span>Community Impact & Social Responsibility</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Beyond Clinical Care:<br />
            <span className="text-rose-600">Serving Our Communities</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Our commitment extends beyond treating cancer — we're dedicated to prevention, education,
            and supporting patients and families throughout their journey.
          </p>
        </div>

        {/* Impact Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-rose-100 hover:border-rose-300 transition-all">
            <p className="text-3xl md:text-4xl font-bold text-rose-600 mb-2">9+</p>
            <p className="text-sm text-gray-600 font-medium">Major Projects</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-rose-100 hover:border-rose-300 transition-all">
            <p className="text-3xl md:text-4xl font-bold text-rose-600 mb-2">1000+</p>
            <p className="text-sm text-gray-600 font-medium">Women Screened</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-rose-100 hover:border-rose-300 transition-all">
            <p className="text-3xl md:text-4xl font-bold text-rose-600 mb-2">100+</p>
            <p className="text-sm text-gray-600 font-medium">Awareness Camps</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-rose-100 hover:border-rose-300 transition-all">
            <p className="text-3xl md:text-4xl font-bold text-rose-600 mb-2">5000+</p>
            <p className="text-sm text-gray-600 font-medium">Lives Impacted</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <>
            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {projects.map((project) => {
                const Icon = iconMap[project.icon] || Heart;
                return (
                  <Card key={project.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-rose-100 bg-white group" data-testid={`community-project-${project.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-rose-100 transition-colors">
                          <Icon className="w-6 h-6 text-rose-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 leading-tight">{project.name}</h3>
                          {project.tagline && (
                            <p className="text-sm text-rose-600 font-semibold italic mt-1">{project.tagline}</p>
                          )}
                        </div>
                      </div>

                      {project.type && (
                        <Badge variant="outline" className="text-xs border-rose-200 text-rose-600 mb-3">{project.type}</Badge>
                      )}

                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{project.description}</p>

                      {project.impact && (
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-2 h-2 bg-rose-500 rounded-full flex-shrink-0"></div>
                          <p className="text-sm text-gray-500 font-medium italic">{project.impact}</p>
                        </div>
                      )}

                      <div className="flex items-center gap-3">
                        {project.link && (
                          <a href={project.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-rose-600 hover:text-rose-500 font-semibold text-sm transition-colors">
                            Visit <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {project.blog_slug && (
                          <Link to={`/blog/${project.blog_slug}`} className="inline-flex items-center gap-1.5 text-rose-600 hover:text-rose-500 font-semibold text-sm transition-colors">
                            Read More <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Foundation Highlight */}
            <div className="bg-gradient-to-r from-rose-600 via-pink-600 to-rose-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                    <Building className="w-4 h-4" />
                    <span>Founded by Dr. Koyyala</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4">Viswapragna Foundation</h3>
                  <p className="text-base md:text-lg opacity-90 mb-6 leading-relaxed">
                    A non-profit organization dedicated to creating awareness about cancer and autism,
                    providing education, and supporting affected families across Northeast India.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a href="https://www.viswapragnafoundation.org" target="_blank" rel="noopener noreferrer">
                      <Button size="lg" variant="secondary" className="bg-white text-rose-600 hover:bg-gray-100 font-semibold">
                        Visit Foundation <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                    <a href="https://viswapragnafoundation.org/event/necap-2025/" target="_blank" rel="noopener noreferrer">
                      <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold">
                        NECAP 2025 <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                    <p className="text-3xl md:text-4xl font-bold mb-2">State-wide</p>
                    <p className="text-sm opacity-90">Reach & Impact</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                    <p className="text-3xl md:text-4xl font-bold mb-2">Multiple</p>
                    <p className="text-sm opacity-90">Programs Running</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                    <p className="text-3xl md:text-4xl font-bold mb-2">Education</p>
                    <p className="text-sm opacity-90">Core Focus Area</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                    <p className="text-3xl md:text-4xl font-bold mb-2">Support</p>
                    <p className="text-sm opacity-90">For Families</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Grassroots Outreach */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 md:p-12 border border-amber-200">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  <Users className="w-4 h-4" />
                  <span>Grassroots Impact</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Taking Healthcare to Villages & Tribal Areas</h3>
                <p className="text-gray-700 max-w-3xl mx-auto leading-relaxed text-sm md:text-base">
                  Regular awareness camps in remote villages, tribal communities, schools, and colleges across
                  Assam and Nagaland. Breaking myths, educating communities about cancer prevention,
                  and ensuring early detection through grassroots engagement.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 text-center shadow-md">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-6 h-6 text-amber-600" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Villages & Tribal Areas</h4>
                  <p className="text-sm text-gray-600">Reaching underserved communities with vital health information</p>
                </div>
                <div className="bg-white rounded-xl p-6 text-center shadow-md">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building className="w-6 h-6 text-amber-600" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Schools & Colleges</h4>
                  <p className="text-sm text-gray-600">Educating young minds about cancer awareness and prevention</p>
                </div>
                <div className="bg-white rounded-xl p-6 text-center shadow-md">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Megaphone className="w-6 h-6 text-amber-600" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Breaking Myths</h4>
                  <p className="text-sm text-gray-600">Dispelling misconceptions with scientific, evidence-based information</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default CommunityConnect;
